import React, { useState } from 'react';
import { Accommodation, Reservation } from '../types';
import { Bed, CalendarDays, CheckCircle, Clock } from 'lucide-react';

interface AccommodationBoardProps {
  accommodations: Accommodation[];
  reservations: Reservation[];
}

export default function AccommodationBoard({ accommodations, reservations }: AccommodationBoardProps) {
  const [statusFilter, setStatusFilter] = useState<'Todos' | 'Livre' | 'Ocupado' | 'Reservado'>('Todos');
  const today = new Date().toISOString().split('T')[0];

  const getAccommodationStatus = (accId: string) => {
    const activeReservation = reservations.find(
      (res) =>
        res.accommodationId === accId &&
        res.checkInDate <= today &&
        res.checkOutDate >= today &&
        ['Pendente', 'Iniciado'].includes(res.status)
    );

    if (!activeReservation) return { status: 'Livre', color: 'bg-emerald-100 text-emerald-800' };
    if (activeReservation.status === 'Iniciado') return { status: 'Ocupado', color: 'bg-rose-100 text-rose-800' };
    return { status: 'Reservado', color: 'bg-amber-100 text-amber-800' };
  };

  const filteredAccommodations = accommodations.filter(acc => {
    if (statusFilter === 'Todos') return true;
    return getAccommodationStatus(acc.id).status === statusFilter;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <h2 className="text-xl font-bold text-zinc-900">Mapa de Acomodações</h2>
        <div className="flex gap-2 justify-start sm:justify-end overflow-x-auto pb-1">
          {(['Todos', 'Livre', 'Ocupado', 'Reservado'] as const).map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                statusFilter === status 
                  ? 'bg-zinc-900 text-white' 
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredAccommodations.map((acc) => {
          const { status, color } = getAccommodationStatus(acc.id);
          return (
            <div key={acc.id} className="bg-white p-4 rounded-xl border border-zinc-200 shadow-sm flex flex-col gap-3">
              <div className="flex justify-between items-start">
                <h3 className="font-bold text-zinc-900">Quarto {acc.number}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${color}`}>
                  {status}
                </span>
              </div>
              <p className="text-xs text-zinc-500 truncate">{acc.name}</p>
              <div className="mt-auto pt-2 flex items-center justify-between text-xs text-zinc-500 border-t border-zinc-100">
                <span className="flex items-center gap-1"><Bed className="w-3 h-3" /> {acc.capacity}</span>
                <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> R$ {acc.rate}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
